import {useEffect, useState, useRef} from 'react';
// TODO: this is a nightnmare - we need to have a user preference context / gobal state management
// just added even more complexity with the localStorage fallback for unauthenticated users (public or token)

import {
	getLocalStorageValue as _getLocalStorageValue,
	setLocalStorageValue as _setLocalStorageValue,
} from '@netx/core-next/utils/browser';

// NOTE: ultimately we should have a user preference context but atm it is not worth doing since we
// are so far from having our entire app wrapped/converted to react
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import {setPreference} from '@netx/core-user-next/utils/setPreference';
import {isPublicUser} from '@netx/core-user-next/utils/isPublicUser';
import {useUserPreferencesStore} from '@netx/core-user-next/store/userPreferences';
import { useSessionStore } from '@netx/core-auth-next/store';
import {useConstCallback} from './useConstCallback';
import useWindowEvent from './useWindowEvent';

type PrefValue<Bool extends boolean = false> = Bool extends true
	? boolean
	: string | undefined;
type PrefInValue<Bool extends boolean = false> = Bool extends true
	? boolean
	: any | undefined;
type PrefOutValue<Bool extends boolean = false> = Bool extends true
	? boolean
	: boolean | string | number | undefined;
type ParseInFn<Bool extends boolean = false, I = PrefInValue<Bool>> = (
	value?: PrefValue<Bool>,
) => I;
type ParseOutFn<
	Bool extends boolean = false,
	I = PrefInValue<Bool>,
	O = PrefOutValue<Bool>,
> = (value: I) => O;

type DefaultValue<
	Bool extends boolean = false,
	I = PrefInValue<Bool>,
> = I extends undefined ? PrefValue<Bool> : I;

type IExtends<Bool extends boolean = false> = PrefInValue<Bool> | undefined;
type OExtends<Bool extends boolean = false> = PrefOutValue<Bool> | undefined;

type Options<
	Bool extends boolean = false,
	I extends IExtends<Bool> = undefined,
	O extends OExtends<Bool> = PrefOutValue<Bool>,
	// D extends DefaultValue<Bool, I> | undefined = undefined,
> = {
	asBoolean?: Bool;
	defaultValue?: DefaultValue<Bool, I>;
	onChange?: (value: PrefValue<Bool>) => void;
	parseOut?: ParseOutFn<Bool, I extends undefined ? PrefValue<Bool> : I, O>;
	parseIn?: ParseInFn<Bool, I>;
}

const getLocalStorageValue = (
	preference: string,
	asBoolean: boolean,
	defaultValue?: any,
) => {
	const value = _getLocalStorageValue(preference);
	if (value == null) return defaultValue ?? (asBoolean ? false : undefined);
	return asBoolean ? value.toLowerCase() === 'true' : value;
};

const setLocalStorageValue = (
	preference: string,
	value: any,
	asBoolean: boolean,
) => {
	_setLocalStorageValue(
		preference,
		value != null ? value.toString() : asBoolean ? 'false' : '',
	);
};

/**
 * Get and track value for a user preference
 * @param preference User preference
 * @param asBoolean Value as boolean
 * @example
   const metadataLookupsEnabled = useUserPref('category.metadataDatasetLookupEnabled', true);
 */
export function useUserPref<
	Bool extends boolean = false,
	I extends IExtends<Bool> = undefined,
	O extends OExtends<Bool> = PrefOutValue<Bool>,
>(preference = '', options?: Options<Bool, I, O>) {
	const {asBoolean, parseIn, parseOut, defaultValue = null} = options || {};

	const parseInRef = useRef(parseIn);
	parseInRef.current = parseIn;

	const {
		isToken
	} = useSessionStore();

	const _getPreference = useConstCallback(() => {
		return isToken || isPublicUser()
			? getLocalStorageValue(preference, asBoolean === true, defaultValue)
			: getPreference(preference, asBoolean);
	});

	const _setUserPreference = useConstCallback((value: O | PrefValue<Bool>) => {
		if (preference === '') return;
		return isToken || isPublicUser()
			? setLocalStorageValue(preference, value, asBoolean === true)
			: setPreference(preference, value);
	});

	const [value, setValue] = useState<I extends undefined ? PrefValue<Bool> : I>(
		parseIn ? parseIn(_getPreference() as PrefValue<Bool>) : _getPreference(),
	);

	const update = useConstCallback(() => {
		if (preference === '') return;
		const prefValue = _getPreference();
		setValue(
			parseInRef.current
				? parseInRef.current(prefValue)
				: prefValue == null
				? defaultValue
				: prefValue,
		);
	});

	useWindowEvent('storage', update, undefined, !isToken);

	useEffect(() => {
		if (preference === '') return;
		const dispose = useUserPreferencesStore.subscribe(update);
		// update();
		return () => {
			dispose();
		};
	}, [preference, asBoolean, defaultValue]);

	const setPref = useConstCallback(
		(newValue: typeof value | ((newValue: typeof value) => typeof value)) => {
			const _value =
				typeof newValue === 'function'
					? (newValue as (newValue: typeof value) => typeof value)(value)
					: newValue;
			const prefValue = parseOut ? parseOut(_value) : _value;
			setValue(_value);
			_setUserPreference(prefValue);
		},
	);

	const unsetPref = useConstCallback(() => {
		setValue(parseIn ? parseIn(undefined) : undefined as any);
		_setUserPreference('' as any);
	});

	return [value, setPref, unsetPref] as const;
}
export default useUserPref;
